/******************************************************************************
 *** BgtSpcExpndBgtFrmt ** 특별회계기금 예산 편성 * 지출 예산 편성
*******************************************************************************/
import React from "react";
import AppContext from "@/js/common/AppContext";
import Button from "@/js/common/ui/Button";
import Util from "@/js/common/Util";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";
import Axios from "@/js/common/Axios";

interface Props {
    ognzCd: string;
    acctYear: string;
    callTime: Date;
    systemCode: string;
    setCallTime: Function;
    itemDpstAmtSum: string;     // 수입예산 편성금액
    setItemDpstAmtSum: Function;
}

const BgtSpcExpndBgtFrmt = ({ ognzCd, acctYear, callTime, systemCode, setCallTime, itemDpstAmtSum, setItemDpstAmtSum }: Props) => {
    const [dataArr, setDataArr] = React.useState<any[]>([]);
    const [sumFrmtAmt, setSumFrmtAmt] = React.useState<string>("");
    const [sumImplPrnmntAmt, setSumImplPrnmntAmt] = React.useState<string>("");
    const [sumImplAmt, setSumImplAmt] = React.useState<string>("");
    const [sumFrmtBlncAmt, setSumFrmtBlncAmt] = React.useState<string>("");
    const [cfmtnYn, setCfmtnYn] = React.useState<string>("N"); // 수입 예산 확정여부
    const [ddlnYn , setDdlnYn ] = React.useState<string>("N"); // 회계연도 마감여부

    React.useEffect(() => {
        AppContext.showSpinner();

        Axios.dataAccess({
            url: "system/bgt/spc/frmt/select-bgt-spc-bgt-frmt.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                orgCd: ognzCd,
                bgtYear: acctYear,
                spcAcntgSeCd: systemCode,
                bgtSbjctSeCd: "02",
                bgtSeCd: systemCode,    // 수입 예산편성 확정여부
                acntgYear: acctYear, // 회계연도 마감여부 판단
            },
            onSuccessFn: (res: any) => {
                setDataArr(res.result.list);
                setSumFrmtAmt(res.result.listSum.frmtAmt);
                setSumImplPrnmntAmt(res.result.listSum.implPrnmntAmt);
                setSumImplAmt(res.result.listSum.implAmt);
                setSumFrmtBlncAmt(res.result.listSum.frmtBlncAmt);

                setItemDpstAmtSum(res.result.itemDpstAmtSum ); // 수입 예산 편성금액
                setCfmtnYn(res.result.detail?.cfmtnYn ?? "N"); // 수입예산 확정여부
                setDdlnYn (res.result.item.ddlnYn           ); // 회계연도 마감여부

                if (res.result.detail?.cfmtnYn !== "Y") {
                    AppContext.openAlert({
                        title: "지출예산 편성 불가",
                        msg: "수입 예산을 확정하신 후 지출 예산을 편성할 수 있습니다.",
                        icon: "warning",
                    });
                }
                AppContext.hideSpinner();
            },
        });
    }, [ognzCd, acctYear, systemCode, callTime, setItemDpstAmtSum]);

  const doChangeAmount = (index: number, newValue: string) => {
    const newDataArr = [...dataArr];
    newDataArr[index].frmtAmt = Util.uncomma(newValue);

    const frmtNum = Number(Util.uncomma(newValue)) || 0;
    const implNum = Number(Util.uncomma(String(newDataArr[index].implAmt))) || 0;
    newDataArr[index].frmtBlncAmt = frmtNum - implNum;

    setDataArr(newDataArr);

    const nextSumFrmtAmt = newDataArr.reduce((acc, row) => {
      return acc + (Number(Util.uncomma(String(row.frmtAmt))) || 0);
    }, 0);
    setSumFrmtAmt(String(nextSumFrmtAmt));

    const implSumNum = Number(Util.uncomma(String(sumImplAmt))) || 0;
    setSumFrmtBlncAmt(String(nextSumFrmtAmt - implSumNum));
  };

    const doSave = () => {
        if (cfmtnYn !== "Y") {
            AppContext.openAlert({
                title: "저장 불가",
                msg: "수입 예산 확정 후 편성할 수 있습니다.",
                icon: "warning",
            });
            return;
        }

        const invalid = dataArr.some((row, idx) => {
            const frmt = Number(Util.uncomma(String(row.frmtAmt))) || 0;
            const prnmnt = Number(Util.uncomma(String(row.implPrnmntAmt))) || 0;
            const impl = Number(Util.uncomma(String(row.implAmt))) || 0;

            if (frmt < prnmnt + impl) {
                AppContext.openAlert({
                    title: "편성금액 확인",
                    msg:
                        `${idx + 1}행 [${row.bgtSbjct1stNm}-${row.bgtSbjct2stNm}-${row.bgtSbjct3stNm}] 편성금액이\n\n` +
                        `(집행예정금액 + 집행금액)의 합계보다 작습니다.`,
                    icon: "warning",
                });
                return true;
            }
            return false;
        });

        if (invalid) return;

        const expSumNum = Number(Util.uncomma(String(sumFrmtAmt))) || 0; // 지출 편성예산 금액
        const incomeSumNum = Number(Util.uncomma(String(itemDpstAmtSum))) || 0; // 수입 편성예산 금액

        if (expSumNum !== incomeSumNum) {
            AppContext.openAlert({
                title: "지출 예산 편성금액 확인",
                msg: `지출 예산 편성금액(${Util.comma(String(expSumNum))})이\n` +
                     `수입 예산 편성금액(${Util.comma(String(incomeSumNum))})과 일치하지 않습니다.`,
                icon: "warning",
            });
            return;
        }

        AppContext.openAlert({
            title: "지출 예산 저장",
            msg: "지출 예산을 저장하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: "저장",
            handleConfirm: () => {
                AppContext.showSpinner();

                Axios.dataAccess({
                    url: "system/bgt/spc/frmt/save-bgt-spc-bgt-frmt.do",
                    methodType: "post",
                    paramType: "json",
                    paramData: {
                        spcAcntgSeCd: systemCode,
                        dataList: dataArr,
                    },
                    onSuccessFn: () => {
                        AppContext.hideSpinner();
                        AppContext.openAlert({
                            title: "지출 예산 저장",
                            msg: "지출 예산이 저장되었습니다.",
                            icon: "check",
                        });
                        setCallTime(new Date());
                    },
                });
            },
        });
    };

  return (
        <>
            <div className="dpFlx aiC jcSb mb10 h44">
                <div className="fm-b fs16 fc007bff ls1 fw700">지출 예산 목록</div>
                {ddlnYn === "Y" &&
                    <span className="fcCrimson fs16 fm-c">{acctYear}년 회계연도는 마감되었습니다.</span>
                }
                <div className="dpFlx aiC gap10">
                    <Button buttonName="예산 저장"
                        width={120} height={40}
                        fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                        variant="styCompleteBtn"
                        borderRadius={6}
                        buttonClass="dpFlx aiC jcC"
                        onClickFunc={doSave}
                        disabled={ddlnYn === "Y" ? true : false}
                    />
                </div>
            </div>

            <div className="w100p bsBdb bgcFDFCFC">
                <div className="w100p"
                    style={{maxHeight: 540, overflowY: "auto", overflowX: "hidden", borderTop: "1px solid #C5C5C5", position: "relative", zIndex: 0,}}
                >
                    <table className="w100p tlF tbl-collapse">
                        <colgroup>
                            <col className="w50" />
                            <col className="w120" />
                            <col className="w120" />
                            <col className="w180" />
                            <col className="w200" />
                            <col className="w150" />
                            <col className="w150" />
                            <col className="w150" />
                        </colgroup>

                        <thead>
                            <tr>
                                {["번호", "관", "항", "목", "편성 금액", "집행 예정 금액", "집행 금액", "집행 잔액"].map((label) => (
                                    <th key={label} className="p0 bd1 bdcEDEDED" style={{ position: "sticky", top: 0, zIndex: 5 }}>
                                        <div className="h40 dpFlx aiC jcC pw12 bgcF1F8FF fm-Pretendard-Medium fs14 fw400 fc34495e ls1 wsNw ofH toE">
                                            {label}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                        {dataArr.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="p0 bd1 bdcEDEDED">
                                    <div className="ph25 taC fc999999 fm-Pretendard-Regular fs14 ls075">조회된 데이터가 없습니다.</div>
                                </td>
                            </tr>
                        ) : (
                            dataArr.map((row, index) => (
                                <tr key={index} className="bgcF9F9F9-hover transitionBgc">
                                    <td className="p0 bd1 bdcEDEDED">
                                        <div className="h35 dpFlx aiC pw12 fm-Pretendard-Light fs14 fc666666 ls075 wsNw ofH toE jcC">
                                            {index + 1}
                                        </div>
                                    </td>
                                    <td className="p0 bd1 bdcEDEDED">
                                        <div className="h35 dpFlx aiC pw12 fm-Pretendard-Light fs14 fc666666 ls075 wsNw ofH toE">
                                            {row.bgtSbjct1stNm}
                                        </div>
                                    </td>
                                    <td className="p0 bd1 bdcEDEDED">
                                        <div className="h35 dpFlx aiC pw12 fm-Pretendard-Light fs14 fc666666 ls075 wsNw ofH toE">
                                            {row.bgtSbjct2stNm}
                                        </div>
                                    </td>
                                    <td className="p0 bd1 bdcEDEDED">
                                        <div className="h35 dpFlx aiC pw12 fm-Pretendard-Light fs14 fc666666 ls075 wsNw ofH toE">
                                            {row.bgtSbjct3stNm}
                                        </div>
                                    </td>
                                    <td className="p0 bd1 bdcEDEDED">
                                        <div className="h35 dpFlx aiC jcE pw12">
                                            <NumberTextBox value={row.frmtAmt}
                                                maxLength={12}
                                                fontFamily="fm-c" fontSize={13} color="#121212" letterSpacing={-1}
                                                borderColor="#BFBFBF" borderOnColor="#111"
                                                thousandsGroupStyle="thousand" thousandSeparator=","
                                                placeholder="0"
                                                className={`w200 h30 ml10 bd1 bdrd2 ${ddlnYn === "Y" ? "bgcF6F6F6" : "bgcWhite"}`}
                                                inputClass={"taR mr5"}
                                                onChange={(v: string) => doChangeAmount(index, v)}
                                                disabled={ddlnYn === "Y" ? true : false}
                                            />
                                        </div>
                                    </td>
                                    <td className="p0 bd1 bdcEDEDED">
                                        <div className="h35 dpFlx aiC jcE pw12 fm-Pretendard-Light fs15 fc666666 ls075 wsNw ofH toE taR">
                                            {Util.comma(String(row.implPrnmntAmt))}
                                        </div>
                                    </td>
                                    <td className="p0 bd1 bdcEDEDED">
                                        <div className="h35 dpFlx aiC jcE pw12 fm-Pretendard-Light fs15 fc666666 ls075 wsNw ofH toE taR">
                                            {Util.comma(String(row.implAmt))}
                                        </div>
                                    </td>
                                    <td className="p0 bd1 bdcEDEDED">
                                        <div className="h35 dpFlx aiC jcE pw12 fm-Pretendard-Light wsNw ofH toE taR">
                                            <NumberTextBox value={row.frmtBlncAmt}
                                                className="w200 h30 ml10"
                                                thousandsGroupStyle="thousand" thousandSeparator=","
                                                placeholder="0"
                                                fontSize={13} fontFamily="fm-c" letterSpacing={-1} color={Number(row.frmtBlncAmt) < 0 ? "#E60000" : ""}
                                                onChange={() => {}}
                                                inputClass={"taR"}
                                                disabled={true}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>

                        <tfoot>
                            <tr className="bgcDCDCDC" style={{ position: "sticky", bottom: 0, zIndex: 4 }}>
                                <td colSpan={4} className="p0 bd1 bdcEDEDED">
                                    <div className="h35 dpFlx aiC jcC pw12 fm-b fs14 ls06 fc111111 wsNw">합계</div>
                                </td>
                                <td className="p0 bd1 bdcEDEDED">
                                    <span className="h35 dpFlx aiC jcE pw12 mr7">{Util.comma(sumFrmtAmt)}</span>
                                </td>
                                <td className="p0 bd1 bdcEDEDED">
                                    <span className="h35 dpFlx aiC jcE pw12">{Util.comma(sumImplPrnmntAmt)}</span>
                                </td>
                                <td className="p0 bd1 bdcEDEDED">
                                    <span className="h35 dpFlx aiC jcE pw12">{Util.comma(sumImplAmt)}</span>
                                </td>
                                <td className="p0 bd1 bdcEDEDED">
                                    <span className="h35 dpFlx aiC jcE pw12">{Util.comma(sumFrmtBlncAmt)}</span>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </>
    );
};

export default BgtSpcExpndBgtFrmt;
